import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { RejectionsCategoriesInsight } from 'apis/merchant-gateway/insights';
import { typographyCss } from 'common/typography';
import { useInsightSubRoutes } from 'modules/insights-v2/hooks/useInsightsSubRoutes';
import { InsightEntity, InsightResponse, MetricRow, RowType } from 'modules/insights-v2/types';
import { extractRejectionCategory } from 'modules/insights-v2/utils';

import SummaryTable from '../InsightSummary/SummaryTable';

type RejectionsCategoriesProps = {
  rejectionCategoriesInsight: InsightResponse<RejectionsCategoriesInsight>;
  insightEntityType: InsightEntity['type'];
};

const ShowAllRejections = styled(Link)`
  ${typographyCss.Body2};
  font-weight: 500;
  text-align: end;
`;

const RejectionsCategoriesTable = ({ rejectionCategoriesInsight, insightEntityType }: RejectionsCategoriesProps) => {
  const rejectionCategoriesData = rejectionCategoriesInsight.data?.data.rejections.split;

  const { formatMessage } = useIntl();
  const { rejectedPurchasesLink } = useInsightSubRoutes();

  const rows: MetricRow[] = (rejectionCategoriesData || []).map(item => ({
    title: extractRejectionCategory({ label: item.label }),
    type: RowType.Metric,
    metrics: item.total.orders,
  }));

  return (
    <SummaryTable
      isFetching={rejectionCategoriesInsight.isFetching}
      isError={
        rejectionCategoriesInsight.isError ||
        (rejectionCategoriesInsight.isLoading && !rejectionCategoriesInsight.isFetching)
      }
      onRetry={rejectionCategoriesInsight.refetch}
      title={formatMessage({ id: 'insights.sales-by-venue.rejections' })}
      tooltip={formatMessage({ id: 'insights.rejections.tooltip' })}
      link={
        ['venue', 'merchant'].includes(insightEntityType) ? (
          <ShowAllRejections to={rejectedPurchasesLink}>
            <FormattedMessage id="insights.rejections.show" />
          </ShowAllRejections>
        ) : undefined
      }
      rows={rows}
    />
  );
};

export default RejectionsCategoriesTable;
